<template>
  <section class="admin-content" id="hefrias-links-page">
    <Navbar />
    <main class="admin-main">
      <div class="page-header">
        <div class="container">
          <div class="d-flex align-items-center text-white" style="padding: 32px 0; gap: 12px;">
            <router-link to="/assessments" class="btn btn-sm btn-outline-light" aria-label="Back to assessments"><i class="fe fe-arrow-left"></i></router-link>
            <div class="header-icon-wrap"><i class="fe fe-link" style="font-size: 22px;"></i></div>
            <div>
              <h4 class="mb-0 font-weight-bold">hefrias link</h4>
              <small style="opacity: .8;">Which hefrias agency and facilities your agency and facilities are</small>
            </div>
          </div>
        </div>
      </div>

      <section style="margin-top: -20px;">
        <div class="container pb-5">
          <!-- Agency -->
          <div class="card shadow-sm panel mb-3">
            <div class="card-body d-flex flex-wrap align-items-end" style="gap: 12px;">
              <div class="flex-fill">
                <div class="font-weight-bold"><i class="fe fe-home mr-1"></i>Agency</div>
                <div class="small text-muted" v-if="status && status.agency_link">
                  Linked to <strong>{{ status.agency_link.hefrias_name }}</strong> (hefrias #{{ status.agency_link.hefrias_id }}).
                </div>
                <div class="small text-warning" v-else>Not linked: facilities can't see or fill forms yet.</div>
              </div>
              <div>
                <label for="hl-agency" class="small font-weight-bold d-block">hefrias agency</label>
                <select id="hl-agency" v-model="agencyChoice" class="form-control form-control-sm" style="min-width: 240px;">
                  <option value="">Select…</option>
                  <option v-for="a in hefriasAgencies" :key="a.id" :value="a.id">{{ a.name }}{{ a.state ? " — " + a.state.name : "" }}</option>
                </select>
              </div>
              <button type="button" class="btn btn-sm btn-submit" :disabled="!agencyChoice || savingAgency" @click="linkAgency">Save</button>
              <button type="button" class="btn btn-sm btn-light" :disabled="syncing" @click="syncItems">
                <i class="fe fe-refresh-cw mr-1"></i>Sync standard items
              </button>
            </div>
            <div v-if="agencyError" class="alert alert-danger small mx-3">{{ agencyError }}</div>
          </div>

          <!-- Facilities -->
          <div class="card shadow-sm panel" v-if="status && status.agency_link">
            <div class="d-flex align-items-center flex-wrap px-4 pt-4 pb-2" style="gap: 8px;">
              <h6 class="mb-0 font-weight-bold mr-2">Facilities</h6>
              <label for="hl-q" class="sr-only">Search facilities</label>
              <input id="hl-q" v-model="q" type="search" class="form-control form-control-sm" style="max-width: 240px;" placeholder="Search name…" @keyup.enter="load(1)" />
              <label for="hl-linked" class="sr-only">Link status</label>
              <select id="hl-linked" v-model="linked" class="form-control form-control-sm" style="max-width: 160px;" @change="load(1)">
                <option value="">All</option>
                <option value="1">Linked</option>
                <option value="0">Not linked</option>
              </select>
              <small class="text-muted ml-auto">Facilities are also linked automatically on their first submission (match, else created in hefrias).</small>
            </div>
            <div class="table-responsive">
              <table class="table align-td-middle mb-0">
                <thead style="background:#f8fafc;">
                  <tr>
                    <th class="table-th">Facility</th>
                    <th class="table-th">hefrias facility</th>
                    <th class="table-th"><span class="sr-only">Actions</span></th>
                  </tr>
                </thead>
                <tbody>
                  <template v-for="f in rows">
                    <tr :key="f.id" class="table-row">
                      <td class="px-4 py-3">
                        <div class="font-weight-bold">{{ f.agency_name }}</div>
                        <div class="small text-muted">{{ f.phc_type || "" }}</div>
                      </td>
                      <td class="py-3">
                        <template v-if="f.hefrias_id">
                          {{ f.hefrias_name }} <span class="small text-muted">#{{ f.hefrias_id }}</span>
                          <div class="small text-muted text-capitalize">{{ f.method }}<span v-if="f.match_score"> · {{ f.match_score }}% match</span></div>
                        </template>
                        <span v-else class="text-muted">Not linked</span>
                      </td>
                      <td class="py-3 pr-4 text-right text-nowrap">
                        <button type="button" class="btn btn-sm btn-view" @click="toggle(f)">{{ open === f.id ? "Close" : f.hefrias_id ? "Change" : "Link" }}</button>
                        <button v-if="f.hefrias_id" type="button" class="btn btn-sm btn-link text-danger" @click="unlink(f)">Unlink</button>
                      </td>
                    </tr>
                    <tr v-if="open === f.id" :key="f.id + '-c'">
                      <td colspan="3" class="px-4 pb-3 cand-cell">
                        <div v-if="loadingCandidates" class="small text-muted">Looking for matches in hefrias…</div>
                        <template v-else>
                          <div v-if="candidateError" class="alert alert-danger small">{{ candidateError }}</div>
                          <div v-for="c in candidates" :key="c.id" class="cand">
                            <div class="flex-fill">
                              <strong>{{ c.name }}</strong> <span class="small text-muted">#{{ c.id }}</span>
                              <div class="small text-muted">{{ c.lga ? c.lga.name : "" }}{{ c.care_level ? " · " + c.care_level : "" }}</div>
                            </div>
                            <span class="match" :class="{ 'match--hi': c.match >= 85 }">{{ c.match }}%</span>
                            <button type="button" class="btn btn-sm btn-submit ml-2" :disabled="saving" @click="link(f, c.id)">Link</button>
                          </div>
                          <div v-if="!candidates.length && !candidateError" class="small text-muted mb-2">No similar facility in hefrias.</div>
                          <button type="button" class="btn btn-sm btn-outline-secondary mt-1" :disabled="saving" @click="link(f, null)">
                            <i class="fe fe-plus mr-1"></i>Create it in hefrias instead
                          </button>
                        </template>
                      </td>
                    </tr>
                  </template>
                  <tr v-if="!rows.length && !loading">
                    <td colspan="3" class="text-center py-5 text-muted">No facilities.</td>
                  </tr>
                </tbody>
              </table>
            </div>
            <div class="d-flex justify-content-end align-items-center px-4 py-3" style="gap: 8px;" v-if="lastPage > 1">
              <button type="button" class="btn btn-sm btn-light" :disabled="page <= 1" @click="load(page - 1)">Previous</button>
              <span class="small">Page {{ page }} of {{ lastPage }}</span>
              <button type="button" class="btn btn-sm btn-light" :disabled="page >= lastPage" @click="load(page + 1)">Next</button>
            </div>
          </div>
        </div>
      </section>
    </main>
  </section>
</template>

<script>
import Navbar from "@/views/Navbar.vue";
import { API, errorText } from "./assessments";

export default {
  components: { Navbar },
  data() {
    return {
      status: null,
      hefriasAgencies: [],
      agencyChoice: "",
      agencyError: "",
      savingAgency: false,
      syncing: false,
      rows: [],
      q: "",
      linked: "",
      page: 1,
      lastPage: 1,
      loading: false,
      open: null,
      candidates: [],
      candidateError: "",
      loadingCandidates: false,
      saving: false,
    };
  },
  created() {
    this.loadStatus();
    this.axios
      .get(`${API}/agencies`)
      .then((res) => (this.hefriasAgencies = res.data.data || []))
      .catch((err) => (this.agencyError = errorText(err, "Could not reach hefrias.")));
  },
  methods: {
    toast(msg, ok = true) {
      this.$toasted[ok ? "success" : "error"](msg, { position: "top-center", duration: 4000 });
    },
    loadStatus() {
      return this.axios.get(`${API}/status`).then((res) => {
        this.status = res.data;
        if (!res.data.can_manage_links) this.$router.replace("/assessments");
        if (res.data.agency_link) {
          this.agencyChoice = res.data.agency_link.hefrias_id;
          this.load(1);
        }
      });
    },
    linkAgency() {
      this.savingAgency = true;
      this.agencyError = "";
      this.axios
        .put(`${API}/agency-link`, { hefrias_agency_id: this.agencyChoice })
        .then((res) => {
          this.toast(res.data.message);
          this.loadStatus();
        })
        .catch((err) => (this.agencyError = errorText(err, "Could not link the agency.")))
        .finally(() => (this.savingAgency = false));
    },
    syncItems() {
      this.syncing = true;
      this.axios
        .post(`${API}/standard-items/sync`)
        .then((res) => this.toast(res.data.message))
        .catch((err) => this.toast(errorText(err, "Could not sync."), false))
        .finally(() => (this.syncing = false));
    },
    load(page) {
      this.loading = true;
      this.axios
        .get(`${API}/facility-links`, { params: { page, q: this.q || undefined, linked: this.linked || undefined } })
        .then((res) => {
          this.rows = res.data.data || [];
          this.page = res.data.current_page || 1;
          this.lastPage = res.data.last_page || 1;
        })
        .finally(() => (this.loading = false));
    },
    toggle(f) {
      if (this.open === f.id) {
        this.open = null;
        return;
      }
      this.open = f.id;
      this.candidates = [];
      this.candidateError = "";
      this.loadingCandidates = true;
      this.axios
        .get(`${API}/facility-links/${f.id}/candidates`)
        .then((res) => (this.candidates = res.data.data || []))
        .catch((err) => (this.candidateError = errorText(err, "Could not search hefrias.")))
        .finally(() => (this.loadingCandidates = false));
    },
    link(f, hefriasId) {
      this.saving = true;
      const body = hefriasId ? { hefrias_facility_id: hefriasId } : { create: true };
      this.axios
        .put(`${API}/facility-links/${f.id}`, body)
        .then((res) => {
          this.toast(res.data.message);
          this.open = null;
          this.load(this.page);
        })
        .catch((err) => this.toast(errorText(err, "Could not link the facility."), false))
        .finally(() => (this.saving = false));
    },
    unlink(f) {
      if (!window.confirm(`Unlink ${f.agency_name} from hefrias? Its past assessments stay in hefrias.`)) return;
      this.axios.delete(`${API}/facility-links/${f.id}`).then(() => this.load(this.page));
    },
  },
};
</script>

<style scoped>
.page-header { background: linear-gradient(135deg, #115e59 0%, #0d9488 100%); }
.header-icon-wrap { width: 48px; height: 48px; background: rgba(255,255,255,0.15); border-radius: 12px; display: flex; align-items: center; justify-content: center; flex-shrink: 0; }
.panel { border: none; border-radius: 12px; }
.table-th { font-size: .73rem; text-transform: uppercase; letter-spacing: .04em; color: #64748b; font-weight: 600; border-bottom: 2px solid #e2e8f0; padding: 12px 16px; }
.table-row:hover { background: #f8fafc; }
.cand-cell { background: #f8fafc; }
.cand { display: flex; align-items: center; gap: 8px; background: #fff; border: 1px solid #e2e8f0; border-radius: 8px; padding: 8px 12px; margin-bottom: 6px; }
.match { font-weight: 700; color: #b45309; }
.match--hi { color: #047857; }
.btn-submit { background: linear-gradient(135deg, #115e59, #0d9488); border: none; color: #fff; border-radius: 8px; font-weight: 600; }
.btn-submit:hover { opacity: .9; color: #fff; }
.btn-view { border-radius: 6px; border: 1px solid #e2e8f0; color: #115e59; background: #fff; font-size: .8rem; }
</style>
